import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ClipQueue, CurriculumBuilder } from "@/components/hq/Curriculum";
import {
  CertificationEditor,
  ChecklistEditor,
  HelpEditor,
  MerchEditor,
  RecipeEditor,
  ResourceEditor,
} from "@/components/hq/Vaults";
import { LeadsDashboard, OperatorsRoster, ProductsEditor } from "@/components/hq/Ladder";
import { CredibilityEditor, NurtureEditor } from "@/components/hq/Growth";
import {
  ApplicationsInbox,
  BookingsInbox,
  FaqEditor,
  HeroEditor,
  JourneyEditor,
  LessonsEditor,
  LicenseEditor,
  PackagesEditor,
  ReelsEditor,
  StoriesEditor,
  StorylineEditor,
} from "@/components/hq/Site";
import { CateringPackagesEditor, GalleryFaqEditor, InstagramEditor, MenuEditor } from "@/components/hq/EventsCms";
import { Lock, ShieldCheck } from "lucide-react";

const TABS = [
  ["applications", "Applications", ApplicationsInbox],
  ["bookings", "Bookings", BookingsInbox],
  ["eventmenu", "Event Menu", MenuEditor],
  ["cateringpricing", "Catering Pricing", CateringPackagesEditor],
  ["galleryfaq", "Gallery & FAQ", GalleryFaqEditor],
  ["instagram", "Instagram", InstagramEditor],
  ["hero", "Homepage Hero", HeroEditor],
  ["stories", "Lemonading", StoriesEditor],
  ["journey", "Smash Journey", JourneyEditor],
  ["reels", "Reels", ReelsEditor],
  ["packages", "Operator Packages", PackagesEditor],
  ["license", "License", LicenseEditor],
  ["lessons", "Free Lessons", LessonsEditor],
  ["timeline", "Smash Story", StorylineEditor],
  ["faq", "FAQ", FaqEditor],
  ["curriculum", "Curriculum", CurriculumBuilder],
  ["clips", "Clip Queue", ClipQueue],
  ["products", "Offers & Pricing", ProductsEditor],
  ["credibility", "Proof & Photos", CredibilityEditor],
  ["nurture", "Email Sequence", NurtureEditor],
  ["leads", "Leads", LeadsDashboard],
  ["operators", "Operators", OperatorsRoster],
  ["recipes", "Recipe Vault", RecipeEditor],
  ["resources", "Resource Vault", ResourceEditor],
  ["merch", "Merch Shelf", MerchEditor],
  ["help", "Get Help", HelpEditor],
  ["checklist", "First Event", ChecklistEditor],
  ["certifications", "Certifications", CertificationEditor],
];

const PasscodeGate = ({ onUnlock }) => {
  const { user } = useAuth();
  const [passcode, setPasscode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/hq/unlock", { passcode });
      sessionStorage.setItem("smash_hq_passcode", passcode.trim());
      onUnlock();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-sm px-5 py-24" data-testid="hq-gate">
      <Lock className="text-primary" size={26} />
      <h1 className="neon mt-6 text-4xl">Smash HQ</h1>
      <p className="mt-3 text-sm text-muted-foreground">
        {user?.role === "admin"
          ? "Enter the HQ passcode to unlock internal tools."
          : "This area is for Smash HQ staff only."}
      </p>
      {user?.role === "admin" ? (
        <form onSubmit={submit} className="mt-8 space-y-4">
          <Input
            data-testid="hq-passcode-input"
            type="password"
            inputMode="numeric"
            required
            value={passcode}
            onChange={(e) => setPasscode(e.target.value)}
            className="rounded-sm bg-[#0A0A0A] text-center tracking-[0.5em]"
            placeholder="••••"
          />
          {error && (
            <div data-testid="hq-passcode-error" className="text-sm text-destructive">
              {error}
            </div>
          )}
          <Button
            data-testid="hq-passcode-submit"
            type="submit"
            disabled={busy}
            className="display w-full rounded-sm bg-primary text-lg uppercase text-primary-foreground hover:bg-[#E6C800]"
          >
            {busy ? "Checking..." : "Unlock HQ"}
          </Button>
        </form>
      ) : null}
      <Link
        to="/academy-dashboard"
        data-testid="hq-gate-back"
        className="mono mt-8 inline-block text-[11px] tracking-widest text-muted-foreground hover:text-primary"
      >
        ← OPERATOR ACADEMY
      </Link>
    </div>
  );
};

export default function HQ() {
  const { content } = useData();
  const { user } = useAuth();
  const [unlocked, setUnlocked] = useState(
    () => !!sessionStorage.getItem("smash_hq_passcode")
  );

  if (!unlocked || user?.role !== "admin")
    return <PasscodeGate onUnlock={() => setUnlocked(true)} />;
  if (!content) return <div className="p-10 mono text-sm text-muted-foreground">Loading HQ…</div>;

  return (
    <div className="mx-auto max-w-7xl px-5 py-8 md:px-10 md:py-12" data-testid="hq-page">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="mono text-[11px] tracking-[0.35em] text-destructive">INTERNAL TOOLS</div>
          <h1 className="mt-1 text-5xl uppercase leading-none md:text-6xl">Smash HQ</h1>
          <p className="brand-script text-2xl text-primary">Keep the standard tight.</p>
          <p className="mono mt-2 flex items-center gap-2 text-[11px] text-[#34C759]">
            <ShieldCheck size={12} /> PASSCODE PROTECTED · ADMIN ONLY
          </p>
        </div>
        <Link
          to="/academy-dashboard"
          data-testid="academy-link"
          className="mono text-[11px] tracking-widest text-muted-foreground hover:text-primary transition-colors"
        >
          ← OPERATOR ACADEMY
        </Link>
      </header>

      <Tabs defaultValue="applications" className="mt-10">
        <TabsList className="flex h-auto w-full flex-wrap justify-start gap-2 bg-transparent p-0">
          {TABS.map(([v, label]) => (
            <TabsTrigger
              key={v}
              value={v}
              data-testid={`hq-tab-${v}`}
              className="display rounded-sm border border-border bg-[#141414] px-4 py-2 text-lg uppercase text-muted-foreground data-[state=active]:border-primary data-[state=active]:bg-primary data-[state=active]:text-primary-foreground transition-colors"
            >
              {label}
            </TabsTrigger>
          ))}
        </TabsList>
        <div className="mt-6">
          {TABS.map(([v, , Component]) => (
            <TabsContent key={v} value={v}>
              <Component />
            </TabsContent>
          ))}
        </div>
      </Tabs>
    </div>
  );
}
